import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import {
  BaseTransaction,
  Pagination,
  Response,
  GeneralLedgerTransaction,
} from '@ui/src';
import { nostroTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';
import { StatusUpdate } from '../../../transactions/type';
import ROUTES from '../../../../routes';

export interface NostroListState {
  transactions?: BaseTransaction[];
  pagination?: Pagination;
  expandedTransactionId?: string;
  selectedCount?: number;
  collapseAllRows?: boolean;
}

const initialState: NostroListState = {
  transactions: undefined,
};

export const nostroListSlice = createSlice({
  name: 'nostroList',
  initialState,
  reducers: {
    setExpandedTransactionId: (state, action: PayloadAction<string>) => {
      state.expandedTransactionId = action.payload;
    },
    setCollapseAllRows: (state, action: PayloadAction<boolean>) => {
      state.collapseAllRows = action.payload;
    },
    setSelectedCount: (state, action: PayloadAction<number>) => {
      state.selectedCount = action.payload;
    },
    clearNostroListState: (state) => {
      state.transactions = undefined;
      state.pagination = undefined;
      state.expandedTransactionId = undefined;
      state.selectedCount = undefined;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      nostroTransactionsListApi.endpoints.getNostroList.matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        const nextTransactions = action.payload.data.edges.map((item) => ({
          ...item.node,
          deletedAt: item?.node?.deletedAt || '',
          url: generatePath(ROUTES.NOSTRO_TRANSFER, {
            transactionId: item.node.id,
          }),
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          values: [],
        }));

        state.transactions = nextTransactions;
        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      nostroTransactionsListApi.endpoints.createNostroTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        let nextTransactions: BaseTransaction[] = [];

        if (state.transactions?.length) {
          nextTransactions = ([] as BaseTransaction[]).concat(
            state.transactions,
          );
        }

        nextTransactions.unshift({
          ...action.payload.data,
          deletedAt: action.payload.data?.deletedAt || '',
          url: generatePath(ROUTES.NOSTRO_TRANSFER, {
            transactionId: action.payload.data.id,
          }),
          generalLedgerTransactions: {
            edges: [],
            pageInfo: undefined,
          },
          values: [],
        });

        state.transactions = nextTransactions;

        if (state.selectedCount) {
          state.selectedCount += 1;
        }
      },
    );
    builder.addMatcher(
      nostroTransactionsListApi.endpoints.getNostroListStatuses.matchFulfilled,
      (state, action: PayloadAction<Response<StatusUpdate>>) => {
        const { edges } = action.payload.data;
        const nextTransactions = state?.transactions?.map((item) => {
          const result = { ...item };

          const currentTransactionStatus = edges.find(
            (transaction) => transaction.node.id === result.id,
          );

          if (
            currentTransactionStatus &&
            currentTransactionStatus.node.baseellaStatus !==
              result.baseellaStatus
          ) {
            result.baseellaStatus =
              currentTransactionStatus.node.baseellaStatus;
          }

          return result;
        });

        state.transactions = nextTransactions;
      },
    );
    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transactions) {
          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.payload.data.id) {
              result.baseellaStatus = action.payload.data.baseellaStatus;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (
        state,
        action: PayloadAction<Response<GeneralLedgerTransaction>> & {
          meta: {
            arg: {
              originalArgs: string;
            };
          };
        },
      ) => {
        if (state.transactions) {
          const nextGeneralLedgerTransactions = {
            edges: action.payload.data.edges.map((item) => ({
              cursor: item.cursor,
              node: {
                ...item.node,
                url: generatePath(ROUTES.NOSTRO_TRANSACTION_GL, {
                  glId: item.node.id,
                }),
              },
            })),
            pageInfo: action.payload.data.pageInfo,
          };

          const nextTransactions = state.transactions.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs) {
              result.generalLedgerTransactions = nextGeneralLedgerTransactions;
            }

            return result;
          });

          state.transactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      nostroTransactionsListApi.endpoints.updateNostroTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        state.transactions = state.transactions?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = {
              ...result,
              ...action.payload.data,
            };
          }

          return result;
        });
      },
    );
  },
});

export const {
  clearNostroListState,
  setExpandedTransactionId,
  setSelectedCount,
  setCollapseAllRows,
} = nostroListSlice.actions;

export default nostroListSlice.reducer;
